import { Box, Button, Grid, Paper, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axiosInterceptor from 'src/app/axiosInterceptor';
import { getAgencyConfig } from 'src/features/agencySlice';
import BatchTransferModal from '../device/BatchTransferModal';
import { EditVersionContent } from '../device/EditVersion';
import DeviceInfoCard from './components/DeviceInfoCard';
import MqttLogPanel from './components/MqttLogPanel';
import PhoneSearchResults from './components/PhoneSearchResults';
import SearchBar from './components/SearchBar';
import { PHONE_SEARCH_PAGE_SIZE } from './constants';
import useMqttSocket from './useMqttSocket';
import useStyles from './styles';
import { isPhoneQuery, normalizePhoneQuery } from './utils';

const CustomerCareLookup = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [vehicle, setVehicle] = useState(null);
  const [searching, setSearching] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [infoTabValue, setInfoTabValue] = useState(0);
  const [batchTransferMode, setBatchTransferMode] = useState(null); // null | 'warehouse' | 'end_user'
  const [searchResults, setSearchResults] = useState(null); // null | { query, phone, list, total, page } khi tìm theo SĐT
  const [loadingPage, setLoadingPage] = useState(false);
  const [openingSerial, setOpeningSerial] = useState(null);

  const dispatch = useDispatch();
  const isAdmin = useSelector(state => state.authSlice.isAdmin);
  const isAgency = useSelector(state => state.authSlice.isAgency);
  const agencyId = useSelector(state => state.authSlice.agencyId);
  const allowSwapSubAgency = useSelector(state => state.authSlice.allowSwapSubAgency);
  // Admin chuyển kho toàn hệ thống; đại lý chỉ khi được bật is_allow_swap_sub_agency
  const canWarehouseTransfer = isAdmin || (isAgency && allowSwapSubAgency);

  useEffect(() => {
    if (isAgency && agencyId) dispatch(getAgencyConfig(agencyId));
  }, [dispatch, isAgency, agencyId]);

  const searchRequestRef = useRef(0); // bỏ qua response cũ khi người dùng tìm liên tiếp

  const handlePeriodicPayload = useCallback(payload => {
    setVehicle(prev => (prev ? { ...prev, ...payload } : prev));
  }, []);
  const { socketConnected, messages } = useMqttSocket(vehicle?.serial, handlePeriodicPayload);

  const reloadVehicle = async q => {
    if (!q) return;
    try {
      const res = await axiosInterceptor.get('/vehicles/lookup', { params: { q } });
      if (res.data?.success) {
        setVehicle(res.data.payload?.vehicle || null);
      }
    } catch (e) {
      // bỏ qua lỗi refresh, không chặn luồng thao tác
    }
  };

  const refreshVehicle = () => reloadVehicle(vehicle?.serial || vehicle?.license_plate);

  // Sau khi đổi bảo hành, biển số đã chuyển sang serial mới -> tra cứu lại theo serial mới
  const handleWarrantyReplaced = res =>
    reloadVehicle(res?.replacement_serial || vehicle?.license_plate);

  // Tra cứu chi tiết 1 thiết bị (serial / biển số) -> hiển thị giao diện chi tiết
  const openVehicleDetail = async (q, requestId) => {
    const res = await axiosInterceptor.get('/vehicles/lookup', { params: { q } });
    if (requestId !== searchRequestRef.current) return true;
    if (!res.data?.success) return false;

    const foundVehicle = res.data.payload?.vehicle || null;
    if (!foundVehicle) return false;
    if (foundVehicle.status === 'inactive') {
      setVehicle(null);
      setErrorMsg('Thiết bị chưa được kích hoạt');
    } else {
      setVehicle(foundVehicle);
      setInfoTabValue(0);
    }
    return true;
  };

  // page bắt đầu từ 1 (theo API)
  const fetchPhoneSearch = async (phone, page) => {
    const res = await axiosInterceptor.get('/vehicles/lookup/search', {
      params: { q: phone, page, page_size: PHONE_SEARCH_PAGE_SIZE }
    });
    if (!res.data?.success) return { list: [], total: 0, page };
    const list = res.data.payload?.devices || [];
    return { list, total: res.data.meta?.total ?? list.length, page: res.data.meta?.page || page };
  };

  // text: giá trị từ SearchBar (chọn gợi ý lịch sử) - tránh đọc state query chưa kịp cập nhật
  const handleSearch = async text => {
    const q = (typeof text === 'string' ? text : query).trim();
    if (!q) {
      setErrorMsg('Vui lòng nhập biển số xe, serial thiết bị hoặc số điện thoại');
      return;
    }

    const requestId = ++searchRequestRef.current;
    setSearching(true);
    setErrorMsg('');
    setVehicle(null);
    setSearchResults(null);
    try {
      if (isPhoneQuery(q)) {
        const phone = normalizePhoneQuery(q);
        const { list, total, page } = await fetchPhoneSearch(phone, 1);
        if (requestId !== searchRequestRef.current) return;

        if (total === 1 && list.length === 1) {
          // Chỉ 1 thiết bị -> mở chi tiết luôn, không bắt người dùng click thêm
          const row = list[0];
          const found = await openVehicleDetail(row.serial || row.license_plate, requestId);
          if (!found) setErrorMsg('Không tìm thấy thiết bị');
          return;
        }
        if (list.length > 1) {
          setSearchResults({ query: q, phone, list, total, page });
          return;
        }
        // Không khớp SĐT -> thử như serial/biển số (serial dạng số bắt đầu bằng 0)
        const found = await openVehicleDetail(q, requestId);
        if (!found) setErrorMsg('Không tìm thấy thiết bị nào theo số điện thoại này');
        return;
      }

      const found = await openVehicleDetail(q, requestId);
      if (!found) setErrorMsg('Không tìm thấy thiết bị');
    } catch (err) {
      if (requestId !== searchRequestRef.current) return;
      setVehicle(null);
      setErrorMsg('Không tìm thấy thiết bị');
    } finally {
      if (requestId === searchRequestRef.current) setSearching(false);
    }
  };

  const handleSelectResult = async row => {
    const q = row.serial || row.license_plate;
    if (!q) return;
    const requestId = ++searchRequestRef.current;
    setOpeningSerial(q);
    setErrorMsg('');
    try {
      const found = await openVehicleDetail(q, requestId);
      if (!found) setErrorMsg('Không tìm thấy thiết bị');
    } catch (err) {
      if (requestId === searchRequestRef.current) setErrorMsg('Không tìm thấy thiết bị');
    } finally {
      if (requestId === searchRequestRef.current) setOpeningSerial(null);
    }
  };

  const handleChangeResultPage = async (e, newPage) => {
    if (!searchResults || newPage === searchResults.page) return;
    const requestId = ++searchRequestRef.current;
    setLoadingPage(true);
    setErrorMsg('');
    try {
      const result = await fetchPhoneSearch(searchResults.phone, newPage);
      if (requestId !== searchRequestRef.current) return;
      setSearchResults(prev => ({ ...prev, ...result }));
    } catch (err) {
      if (requestId === searchRequestRef.current) setErrorMsg('Không tải được danh sách thiết bị');
    } finally {
      if (requestId === searchRequestRef.current) setLoadingPage(false);
    }
  };

  const handleBackToResults = () => {
    ++searchRequestRef.current;
    setVehicle(null);
    setErrorMsg('');
  };

  const handleViewOnMap = () => {
    if (!vehicle?.device_id) return;

    const params = new URLSearchParams();

    // Đại lý sở hữu thiết bị
    const deviceAgencyId = vehicle?.agency_id;
    // Tài khoản (end user) sở hữu thiết bị
    const deviceEndUserId = vehicle?.end_user?.id;

    if (deviceAgencyId) params.set('agency_id', deviceAgencyId);
    if (deviceEndUserId) params.set('end_user_id', deviceEndUserId);

    // Biển số dùng để auto chọn (focus) phương tiện trên bản đồ
    const licensePlate = vehicle?.license_plate || '';
    if (licensePlate) params.set('license_plate', licensePlate);

    const search = params.toString();
    navigate(search ? `/app/tracking?${search}` : '/app/tracking');
  };

  return (
    <Box className={classes.root}>
      <SearchBar value={query} onChange={setQuery} onSearch={handleSearch} searching={searching} />

      {errorMsg && (
        <Typography className={classes.errorText}>{errorMsg}</Typography>
      )}

      {!vehicle && searchResults && (
        <PhoneSearchResults
          results={searchResults}
          loadingPage={loadingPage}
          openingSerial={openingSerial}
          onSelect={handleSelectResult}
          onChangePage={handleChangeResultPage}
        />
      )}

      {vehicle && searchResults && (
        <Button
          size="small"
          className={classes.backButton}
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToResults}
        >
          Quay lại danh sách ({searchResults.total} thiết bị)
        </Button>
      )}

      {vehicle && (
        <Grid container spacing={2} className={classes.contentGrid}>

          {/* Cột 1: Thông tin thiết bị + Thông tin tài khoản + nút xem trên bản đồ */}
          <Grid item md={3} xs={12} className={classes.gridCol}>
            <DeviceInfoCard
              vehicle={vehicle}
              tabValue={infoTabValue}
              onChangeTab={setInfoTabValue}
              isAdmin={isAdmin}
              canWarehouseTransfer={canWarehouseTransfer}
              onBatchTransfer={setBatchTransferMode}
              onViewOnMap={handleViewOnMap}
              canWarrantyReplace={isAdmin}
              onWarrantyReplaced={handleWarrantyReplaced}
              onDriverLoginSuccess={refreshVehicle}
            />
          </Grid>

          {/* Cột 2: Cấu hình thiết bị (3 tab như modal EditVersion) */}
          <Grid item md={6} xs={12} className={classes.gridCol}>
            <Paper className={`${classes.card} ${classes.configCard}`}>
              {vehicle.device_id ? (
                <EditVersionContent
                  device={vehicle}
                  fetchDetail={refreshVehicle}
                  onSaved={refreshVehicle}
                />
              ) : (
                <Typography className={classes.emptyConfigText}>
                  Thiết bị chưa có dữ liệu để cấu hình.
                </Typography>
              )}
            </Paper>
          </Grid>

          <Grid item md={3} xs={12} className={classes.gridCol}>
            <MqttLogPanel socketConnected={socketConnected} messages={messages} />
          </Grid>
        </Grid>
      )}

      {canWarehouseTransfer && (
        <BatchTransferModal
          open={Boolean(batchTransferMode)}
          mode={batchTransferMode}
          defaultSerial={vehicle?.serial}
          onClose={() => setBatchTransferMode(null)}
          onSuccess={refreshVehicle}
        />
      )}
    </Box>
  );
};

export default React.memo(CustomerCareLookup);
